'use client';

import Link from 'next/link';
import { useRef, useState } from 'react';
import { useEventListener } from '@/hooks/useEventListener';
import { dispatchWindowCustomEvent, getBrowserWindow } from '@/lib/utils/browser-window';

type QuotaBannerPayload = { message?: string | undefined };
type QuotaBannerEvent = CustomEvent<QuotaBannerPayload>;

const isQuotaBannerEvent = (event: Event): event is QuotaBannerEvent => event instanceof CustomEvent;

const DEFAULT_MESSAGE =
  "You've reached your free analysis limit. Upgrade to Pro for unlimited analyses and extended reports.";

export function showQuotaBanner(message?: string) {
  dispatchWindowCustomEvent<QuotaBannerPayload>('quota:show', {
    message,
  });
}

export function QuotaBanner() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string>(DEFAULT_MESSAGE);
  // Gate: only show once per page session
  const shownOnceReference = useRef(false);

  const windowTarget = getBrowserWindow();
  useEventListener(windowTarget, 'quota:show', (event) => {
    if (shownOnceReference.current) {
      return;
    }
    if (!isQuotaBannerEvent(event)) {
      return;
    }
    const { message: detailMessage } = event.detail ?? {};
    setMessage(detailMessage ?? DEFAULT_MESSAGE);
    setOpen(true);
    shownOnceReference.current = true;
  });

  useEventListener(windowTarget, 'quota:hide', () => {
    setOpen(false);
  });

  if (!open) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-layer-toast">
      <div className="mx-auto max-w-5xl px-3 md:px-4">
        <div className="mt-2 flex items-center justify-between rounded-lg border border-warning/40 bg-warning/10 p-3 shadow-sm text-warning-foreground">
          <div className="text-sm">{message}</div>
          <div className="flex items-center gap-2">
            <Link
              href="/pricing"
              className="rounded-md bg-warning px-3 py-1.5 text-sm font-medium text-warning-foreground hover:bg-warning/80"
            >
              Upgrade to Pro
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
              }}
              className="rounded-md border border-warning/40 px-2.5 py-1.5 text-xs text-warning-foreground hover:bg-warning/20"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
